import { Stack, Typography, Box, Avatar, alpha, Skeleton, Tooltip, TooltipProps, tooltipClasses, styled } from "@mui/material";
import { useTopCompetQuery } from "src/context/api/non-branded/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { fPercent } from "src/utils/format-number";
import { subWeeks } from "date-fns";
import { useNonBrandedContext } from "./context/use-non-branded";
import useLocales from "src/locales/use-locales";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}));

export default function TopCompetitors() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const { t } = useLocales();

    const { data: topCompetData, isLoading, isFetching, isUninitialized } = useTopCompetQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(subWeeks(new Date(), 12), "yyyy-MM-dd"),
        end_date: fDate(new Date(), "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: filterStore.countries,
        platforms: filterStore.platforms,
        interval: filterStore.timestep,
        search_terms: filterStore.keywords,
    }, { skip: !workspace?.id || filterStore.countries.length === 0 });

    const competitors = topCompetData?.data || [];
    // Sort by percentage descending
    const sortedCompetitors = [...competitors].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));

    return (
        <>
            <Stack className="card-top-header" borderBottom={1} borderColor="divider" direction="row" spacing={3} py={1} px={2} justifyContent="space-between">
                <LightTooltip title={t("dashboard.nonBrand.tooltips.topCompetitors")}>
                    <Typography className="card-top-header-item-label" variant="h6" color="text.primary" sx={{ cursor: 'help' }}>
                        {t("dashboard.nonBrand.columns.topCompetitors")}
                    </Typography>
                </LightTooltip>
                <LightTooltip title={t("dashboard.nonBrand.tooltips.visibilityRate")}>
                    <Typography className="card-top-header-item-value" variant="h6" color="text.primary" sx={{ cursor: 'help' }}>
                        {t("dashboard.nonBrand.columns.visibilityRate")}
                    </Typography>
                </LightTooltip>
            </Stack>
            <Stack overflow="hidden" width={"inherit"} flex={1} spacing={1.5} sx={{ overflowY: 'auto', px: 0, py: 0 }}>
                {isUninitialized || isLoading || isFetching ? (
                    <Skeleton variant="rectangular" width="100%" height={"100%"} />
                ) : (
                    sortedCompetitors.length === 0 ? (
                        <Stack height={"100%"} justifyContent="center" alignItems="center">
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                                {t("dashboard.nonBrand.noCompetitorsDataAvailable")}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction="column" spacing={"12px"} py={"12px"} px={2} height="stretch">
                            {sortedCompetitors.slice(0, 10).map((competitor, index) => {
                                const percentage = competitor.percentage || 0;
                                return (
                                    <Stack direction="column" spacing={1.5} key={`${competitor.domain_name}-${index}`}>
                                        <Stack
                                            direction="row"
                                            key={`${competitor.domain_name}-${index}`}
                                            px={1}
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                borderRadius: 1,
                                                overflow: 'hidden',
                                                height: 34.4,
                                            }}
                                        >
                                            {/* Progress bar background */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    height: '100%',
                                                    width: `${percentage}%`,
                                                    bgcolor: alpha("#919EAB", 0.2),
                                                    borderRadius: 1,
                                                    transition: 'width 0.3s ease',
                                                }}
                                            />

                                            {/* Content */}
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={1.5}
                                                sx={{
                                                    position: 'relative',
                                                    zIndex: 1,
                                                }}
                                            >
                                                {/* Percentage */}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: "#333C47",
                                                        textAlign: "right",
                                                        fontVariantNumeric: "oldstyle-nums tabular-nums",
                                                        fontFamily: "Public Sans",
                                                        fontSize: "13px",
                                                        fontStyle: "normal",
                                                        fontWeight: 400,
                                                        lineHeight: "100%",
                                                    }}
                                                >
                                                    {percentage > 0 && percentage < 1 ? '<1' : Math.round(percentage)}%
                                                </Typography>

                                                {/* Icon */}
                                                <Avatar
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        bgcolor: index === 2 ? '#20D873' : 'background.paper',
                                                        color: index === 2 ? 'common.white' : 'text.primary',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        border: index === 2 ? 'none' : '1px solid',
                                                        borderColor: 'divider',
                                                    }}
                                                    src={`https://www.google.com/s2/favicons?domain=${competitor.domain_name}&sz=32`}
                                                />

                                                {/* Domain name */}
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    sx={{
                                                        color: "#333C47",
                                                        fontFamily: "Public Sans",
                                                        fontSize: "13px",
                                                        fontStyle: "normal",
                                                        fontWeight: 400,
                                                        lineHeight: "100%",
                                                    }}
                                                >
                                                    {competitor.domain_name}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )
                )}
            </Stack>
        </>
    );
}
